/**
 * Admin › User Verification — review pending requests, approve or reject.
 * Also allows admins to add persistent notes on any user document.
 */
import { useEffect, useState, useCallback } from "react";
import { http } from "@/lib/api";
import { toast } from "sonner";
import {
  ShieldCheck, ShieldAlert, Clock3, RefreshCw, CheckCircle2, X,
  User, StickyNote, ExternalLink, Trash2, Send, Sparkles,
} from "lucide-react";

const STATUSES = ["pending", "verified", "rejected", "unverified", "all"];

export default function AdminUserVerifications() {
  const [view, setView] = useState("queue"); // queue | suggestions
  const [status, setStatus] = useState("pending");
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);
  const [minSubs, setMinSubs] = useState(1);

  const loadQueue = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await http.get("/admin/users/verifications", { params: { status } });
      setItems(data.items || []);
      setCounts(data.counts || {});
    } catch {
      toast.error("Failed to load verifications");
    } finally { setLoading(false); }
  }, [status]);

  const loadSuggestions = useCallback(async () => {
    setSuggestionsLoading(true);
    try {
      const { data } = await http.get("/admin/users/verification-suggestions", {
        params: { min_submissions: minSubs, limit: 50 },
      });
      setSuggestions(data.items || []);
    } catch {
      toast.error("Failed to load suggestions");
    } finally { setSuggestionsLoading(false); }
  }, [minSubs]);

  useEffect(() => {
    if (view === "queue") loadQueue();
    else loadSuggestions();
  }, [view, loadQueue, loadSuggestions]);

  const approve = async (uid) => {
    const comment = window.prompt("Approval comment (optional):") || "";
    try {
      await http.post(`/admin/users/${uid}/verify`, { comment: comment || null });
      toast.success("User verified — they've been emailed the good news.");
      loadQueue(); loadSuggestions();
    } catch (e) { toast.error(e?.response?.data?.detail || "Approve failed"); }
  };

  const reject = async (uid) => {
    const reason = window.prompt("Reason for rejection (shown to user):");
    if (!reason) return;
    try {
      await http.post(`/admin/users/${uid}/reject-verification`, { reason });
      toast.success("Verification rejected — user notified by email.");
      loadQueue(); loadSuggestions();
    } catch (e) { toast.error(e?.response?.data?.detail || "Reject failed"); }
  };

  const sendInvite = async (uid) => {
    const adminNote = window.prompt(
      "Optional personal note to include in the email (or leave blank):",
    );
    try {
      const { data } = await http.post(`/admin/users/${uid}/send-verification-invite`, {
        admin_note: adminNote || null,
      });
      toast.success(`Invite sent to ${data.email_to}`);
      loadSuggestions();
    } catch (e) { toast.error(e?.response?.data?.detail || "Send failed"); }
  };

  return (
    <div data-testid="admin-user-verifications">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="overline mb-2">USERS · VERIFICATION</div>
          <h2 className="font-display text-3xl">Contributor verification queue.</h2>
          <p className="mt-1 text-xs opacity-70 max-w-2xl">
            Verified users' submissions (events, memorials, causes) publish
            immediately with no editor review. Review requests carefully — the
            info is provided by the user and stays private.
          </p>
        </div>
        <button onClick={view === "queue" ? loadQueue : loadSuggestions} className="wofy-btn-ghost text-sm" aria-label="Refresh">
          <RefreshCw className="w-4 h-4" />
        </button>
      </div>

      {/* View toggle */}
      <div className="mt-6 flex gap-1.5">
        {[
          { v: "queue", label: "Verification queue" },
          { v: "suggestions", label: "Active unverified · invite" },
        ].map((t) => (
          <button
            key={t.v}
            onClick={() => setView(t.v)}
            data-testid={`vf-view-${t.v}`}
            className={`px-4 py-2 text-sm rounded-full ${view === t.v ? "" : "hover:bg-[color:var(--wofy-surface)]"}`}
            style={view === t.v ? { background: "var(--wofy-primary)", color: "#FDFBF7" } : { border: "1px solid var(--wofy-border)" }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {view === "queue" && (
        <>
          <div className="mt-4 flex flex-wrap gap-1.5">
            {STATUSES.map((s) => (
              <button
                key={s}
                data-testid={`vf-filter-${s}`}
                onClick={() => setStatus(s)}
                className={`px-3 py-1.5 text-xs rounded-full ${status === s ? "" : "hover:bg-[color:var(--wofy-surface)]"}`}
                style={status === s ? { background: "var(--wofy-primary)", color: "#FDFBF7" } : { border: "1px solid var(--wofy-border)" }}
              >
                {s} {counts[s] !== undefined ? `(${counts[s]})` : ""}
              </button>
            ))}
          </div>

          {loading && <div className="mt-6 text-sm opacity-60">Loading…</div>}
          {!loading && items.length === 0 && (
            <div className="mt-6 wofy-card text-center py-10">
              <ShieldCheck className="w-8 h-8 mx-auto opacity-40 mb-2" />
              <div className="font-display text-lg">Queue is clear.</div>
            </div>
          )}

          <div className="mt-6 grid gap-3">
            {items.map((u) => (
              <UserRow
                key={u.id}
                user={u}
                expanded={openId === u.id}
                onToggle={() => setOpenId(openId === u.id ? null : u.id)}
                onApprove={() => approve(u.id)}
                onReject={() => reject(u.id)}
                onSendInvite={() => sendInvite(u.id)}
                onNoteAdded={loadQueue}
              />
            ))}
          </div>
        </>
      )}

      {view === "suggestions" && (
        <SuggestionsPanel
          items={suggestions}
          loading={suggestionsLoading}
          minSubs={minSubs}
          setMinSubs={setMinSubs}
          onSendInvite={sendInvite}
        />
      )}
    </div>
  );
}


function SuggestionsPanel({ items, loading, minSubs, setMinSubs, onSendInvite }) {
  return (
    <div data-testid="verification-suggestions" className="mt-6">
      <div className="wofy-card">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <Sparkles className="w-5 h-5 mt-0.5 shrink-0" style={{ color: "var(--wofy-primary)" }} />
            <div>
              <div className="overline">RETENTION NUDGE</div>
              <h3 className="font-display text-xl mt-1">Invite active unverified contributors.</h3>
              <p className="text-xs opacity-70 mt-1 max-w-2xl">
                Users below have already submitted content but aren't yet Verified. One
                click emails them a branded invite with their activity count baked in
                (e.g. "You've submitted 3 events on Social Pulse — thank you!").
              </p>
            </div>
          </div>
          <label className="text-xs font-mono uppercase opacity-70 flex items-center gap-2">
            MIN SUBMISSIONS
            <input
              type="number" min={1} max={20}
              value={minSubs}
              onChange={(e) => setMinSubs(Math.max(1, parseInt(e.target.value || "1", 10)))}
              data-testid="vf-min-subs"
              className="w-16 px-2 py-1 rounded border bg-transparent"
              style={{ borderColor: "var(--wofy-border)" }}
            />
          </label>
        </div>
      </div>

      {loading && <div className="mt-6 text-sm opacity-60">Loading suggestions…</div>}
      {!loading && items.length === 0 && (
        <div className="mt-6 wofy-card text-center py-10">
          <Sparkles className="w-8 h-8 mx-auto opacity-40 mb-2" />
          <div className="font-display text-lg">No active unverified contributors yet.</div>
          <div className="text-xs opacity-60 mt-1">
            Lower the minimum-submissions threshold, or come back after more users start posting.
          </div>
        </div>
      )}

      <div className="mt-6 grid gap-3">
        {items.map((u) => {
          const c = u.submission_counts || {};
          const already = !!u.invited_at;
          return (
            <div key={u.id} data-testid={`vf-suggestion-${u.id}`} className="wofy-card flex flex-wrap items-center gap-4">
              {u.profile_picture ? (
                <img src={u.profile_picture} alt="" className="w-14 h-14 rounded-full object-cover border" />
              ) : (
                <div className="w-14 h-14 rounded-full grid place-items-center shrink-0" style={{ background: "var(--wofy-surface)" }}>
                  <User className="w-6 h-6 opacity-50" />
                </div>
              )}
              <div className="flex-1 min-w-[220px]">
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="font-display text-lg">{u.name}</div>
                  <span className="text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded-full bg-neutral-100 text-neutral-700">
                    {u.verification_status || "unverified"}
                  </span>
                  {already && (
                    <span className="text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded-full bg-blue-100 text-blue-800">
                      invited
                    </span>
                  )}
                </div>
                <div className="text-xs opacity-70 mt-0.5">
                  {u.email}
                  {(u.city || u.country) && <span> · {[u.city, u.country].filter(Boolean).join(", ")}</span>}
                </div>
                <div className="text-xs mt-1 opacity-80 font-mono">
                  {c.events ? `${c.events} event${c.events > 1 ? "s" : ""}` : null}
                  {c.memorials ? ` · ${c.memorials} memorial${c.memorials > 1 ? "s" : ""}` : null}
                  {c.causes ? ` · ${c.causes} cause${c.causes > 1 ? "s" : ""}` : null}
                </div>
                {u.invited_at && (
                  <div className="text-[10px] font-mono opacity-60 mt-1">
                    Last invited {u.invited_at.slice(0, 10)}
                  </div>
                )}
              </div>
              <button
                onClick={() => onSendInvite(u.id)}
                data-testid={`vf-invite-${u.id}`}
                className="wofy-btn-primary text-xs shrink-0"
              >
                <Send className="w-3.5 h-3.5" /> {already ? "Re-send invite" : "Send invite"}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function UserRow({ user, expanded, onToggle, onApprove, onReject, onSendInvite, onNoteAdded }) {
  const st = user.verification_status || "unverified";
  const req = user.verification_request || null;
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const addNote = async () => {
    if (!note.trim()) return;
    setBusy(true);
    try {
      await http.post(`/admin/users/${user.id}/notes`, { note });
      setNote("");
      toast.success("Note added");
      onNoteAdded?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Note failed"); }
    finally { setBusy(false); }
  };

  const deleteNote = async (noteId) => {
    if (!window.confirm("Delete this note?")) return;
    try {
      await http.delete(`/admin/users/${user.id}/notes/${noteId}`);
      toast.success("Note deleted");
      onNoteAdded?.();
    } catch { toast.error("Delete failed"); }
  };

  const badge = {
    unverified: { cls: "bg-neutral-100 text-neutral-700", Icon: ShieldAlert },
    pending: { cls: "bg-amber-100 text-amber-800", Icon: Clock3 },
    verified: { cls: "bg-emerald-100 text-emerald-800", Icon: ShieldCheck },
    rejected: { cls: "bg-red-100 text-red-800", Icon: ShieldAlert },
  }[st] || { cls: "bg-neutral-100", Icon: User };
  const { Icon } = badge;

  return (
    <div className="wofy-card" data-testid={`vf-row-${user.id}`}>
      <div
        onClick={onToggle}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onToggle(); } }}
        className="w-full flex flex-wrap items-start gap-4 text-left cursor-pointer"
      >
        {user.profile_picture ? (
          <img src={user.profile_picture} alt="" className="w-14 h-14 rounded-full object-cover border" />
        ) : (
          <div className="w-14 h-14 rounded-full grid place-items-center" style={{ background: "var(--wofy-surface)" }}>
            <User className="w-6 h-6 opacity-50" />
          </div>
        )}
        <div className="flex-1 min-w-[240px]">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="font-display text-lg">{user.name}</div>
            <span className={`text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded-full ${badge.cls}`}>
              <Icon className="w-3 h-3 inline mr-1" />{st}
            </span>
            {user.is_admin && <span className="text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded-full bg-blue-100 text-blue-800">admin</span>}
          </div>
          <div className="text-xs opacity-70 mt-0.5">
            {user.email}
            {user.phone && <span> · {user.phone}</span>}
            {(user.city || user.country) && <span> · {[user.city, user.state, user.country].filter(Boolean).join(", ")}</span>}
          </div>
          {req?.organization && <div className="text-xs mt-1 opacity-80">Org: {req.organization}</div>}
          {req?.is_volunteer && <div className="text-xs mt-1 opacity-80">Available to volunteer{req.volunteer_causes?.length ? `: ${req.volunteer_causes.join(", ")}` : ""}</div>}
        </div>
        {st === "pending" && (
          <div className="flex items-center gap-2 shrink-0" onClick={(e) => e.stopPropagation()}>
            <button onClick={onApprove} data-testid={`vf-approve-${user.id}`} className="wofy-btn-primary text-xs">
              <CheckCircle2 className="w-4 h-4" /> Approve
            </button>
            <button onClick={onReject} data-testid={`vf-reject-${user.id}`} className="wofy-btn-ghost text-xs" style={{ color: "#B23A3A" }}>
              <X className="w-4 h-4" /> Reject
            </button>
          </div>
        )}
        {(st === "unverified" || st === "rejected") && !user.is_admin && onSendInvite && (
          <div className="flex items-center gap-2 shrink-0" onClick={(e) => e.stopPropagation()}>
            <button onClick={onSendInvite} data-testid={`vf-invite-queue-${user.id}`} className="wofy-btn-ghost text-xs">
              <Send className="w-3.5 h-3.5" /> Send invite
            </button>
          </div>
        )}
      </div>

      {expanded && (
        <div className="mt-5 pt-5 border-t space-y-4" style={{ borderColor: "var(--wofy-border)" }}>
          {req ? (
            <div className="grid gap-3 md:grid-cols-2 text-xs">
              <Detail label="Full legal name" value={req.full_name} />
              <Detail label="Phone" value={req.phone} />
              <Detail label="Email" value={req.email} />
              <Detail label="LinkedIn" value={req.linkedin_url} link />
              <Detail label="Address" value={[req.street_address, req.city, req.state, req.postal_code, req.country].filter(Boolean).join(", ")} />
              <Detail label="Organization" value={req.organization} />
              <Detail label="ID document" value={req.id_document_type} />
              <Detail label="ID note" value={req.id_document_note} full />
              <Detail label="References" value={req.references} full />
              <Detail label="Volunteer" value={req.is_volunteer ? `Yes — ${(req.volunteer_causes || []).join(", ")}` : "No"} />
              <Detail label="Submitted at" value={req.submitted_at?.slice(0, 16)?.replace("T", " ")} />
            </div>
          ) : (
            <div className="text-xs opacity-70">No verification request on file.</div>
          )}

          {/* Notes */}
          <div className="pt-4 border-t" style={{ borderColor: "var(--wofy-border)" }}>
            <div className="overline mb-2 flex items-center gap-1.5">
              <StickyNote className="w-3.5 h-3.5" /> ADMIN NOTES ({(user.admin_notes || []).length})
            </div>
            <div className="space-y-2">
              {(user.admin_notes || []).map((n) => (
                <div key={n.id} className="text-xs p-3 rounded-lg flex items-start gap-3" style={{ background: "var(--wofy-surface)" }}>
                  <div className="flex-1">
                    <div className="whitespace-pre-line">{n.note}</div>
                    <div className="mt-1 text-[10px] font-mono opacity-60">
                      by {n.author_name} · {n.created_at?.slice(0, 16)?.replace("T", " ")}
                    </div>
                  </div>
                  <button onClick={() => deleteNote(n.id)} className="opacity-60 hover:opacity-100" aria-label="Delete note"><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
              ))}
            </div>
            <div className="mt-3 flex gap-2">
              <input
                data-testid={`vf-note-input-${user.id}`}
                value={note} onChange={(e) => setNote(e.target.value)}
                placeholder="Add a note (private to admins)…"
                className="flex-1 px-3 py-2 rounded-lg border bg-transparent text-sm"
                style={{ borderColor: "var(--wofy-border)" }}
              />
              <button onClick={addNote} disabled={busy || !note.trim()} data-testid={`vf-note-save-${user.id}`} className="wofy-btn-primary text-xs">
                Add note
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Detail({ label, value, full, link }) {
  if (!value) return null;
  return (
    <div className={full ? "md:col-span-2" : ""}>
      <div className="text-[10px] font-mono uppercase opacity-60">{label}</div>
      {link ? (
        <a href={value} target="_blank" rel="noopener noreferrer" className="text-xs underline inline-flex items-center gap-1 break-all">
          {value} <ExternalLink className="w-3 h-3" />
        </a>
      ) : (
        <div className="text-xs mt-0.5 whitespace-pre-line break-words">{value}</div>
      )}
    </div>
  );
}
